import React from "react";
import { BASELINE_ATTRIBUTION, CELL_SIZE_M } from "../../config/exposure";
import type { BaselineLayer } from "../../hooks/emergency/useBaselineLayer";
import type { BaselineMeasure } from "../../services/exposureApi";
import { maxCellValue, useBaselineRamp } from "./BaselineCellLayer";

const card = "rounded-xl border border-paper-300/70 bg-surface/95 shadow-soft backdrop-blur";

const MEASURES: Array<{ id: BaselineMeasure; label: string; legend: string }> = [
  { id: "residents", label: "Residents", legend: "Registered residents" },
  { id: "employees", label: "Employees", legend: "Employees at workplaces" },
];

const segment = (active: boolean) =>
  `rounded-md px-2.5 py-0.5 text-xs font-medium transition-colors ${active ? "bg-brand-500 text-on-brand" : "text-ink-muted hover:bg-paper-100"}`;

/**
 * Map overlay (Emergency only) that switches the "Population baseline" cell layer on and off, picks
 * residents or employees, and shows the legend and the source credit while the layer is on. It sits
 * inside the map, like the other map overlays, so it survives fullscreen.
 */
const BaselineLayerControl: React.FC<{ layer: BaselineLayer }> = ({ layer }) => {
  const ramp = useBaselineRamp();
  const measure = MEASURES.find((m) => m.id === layer.measure)!;
  const cells = layer.data?.cells ?? [];
  return (
    <div className={`${card} flex w-56 flex-col gap-1.5 px-2 py-1.5`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-ink">Population baseline</span>
        <div role="group" aria-label="Population baseline layer" className="inline-flex rounded-lg border border-paper-300 p-0.5">
          {([false, true] as const).map((on) => (
            <button key={String(on)} aria-pressed={layer.on === on} onClick={() => layer.setOn(on)} className={segment(layer.on === on)}>
              {on ? "On" : "Off"}
            </button>
          ))}
        </div>
      </div>

      {layer.on && (
        <>
          <div role="group" aria-label="Cell layer measure" className="inline-flex w-full rounded-lg border border-paper-300 p-0.5">
            {MEASURES.map((m) => (
              <button
                key={m.id}
                aria-pressed={layer.measure === m.id}
                onClick={() => layer.setMeasure(m.id)}
                className={`flex-1 ${segment(layer.measure === m.id)}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {layer.problem ? (
            <p role="status" className="text-[11px] text-bad-ink">{layer.problem}</p>
          ) : layer.loading ? (
            <p role="status" className="text-[11px] text-ink-muted">Loading cells…</p>
          ) : cells.length > 0 ? (
            <div data-testid="baseline-legend">
              <p className="text-[11px] text-ink-muted">{`${measure.legend} per ${CELL_SIZE_M} m cell`}</p>
              <div className="h-2 rounded-sm" style={{ backgroundImage: `linear-gradient(to right, ${ramp.low}, ${ramp.high})` }} />
              <div className="flex justify-between text-[10px] tabular-nums text-ink-faint">
                <span>0</span>
                <span>{maxCellValue(cells).toLocaleString()}</span>
              </div>
            </div>
          ) : null}
          <p className="text-[10px] text-ink-faint">{BASELINE_ATTRIBUTION}</p>
        </>
      )}
    </div>
  );
};

export default BaselineLayerControl;
